import React, { useEffect, useState } from "react";
import { Container, Row, Col, Card, Table } from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import weekday from "dayjs/plugin/weekday";
import isoWeek from "dayjs/plugin/isoWeek";
import "./tradeCalendar.scss";
import { useAppSelector } from "../../app/hooks";
import { useDispatch } from "react-redux";
import { fetchTrade } from "../../features/dashboard/tradeSlice";

dayjs.extend(weekday);
dayjs.extend(isoWeek);

const TradeCalendar = () => {
  const navigate = useNavigate();

  const { config } = useAppSelector((state) => state.user);
  const { isLoading, isSuccess, tradeData } = useAppSelector(
    (state) => state.trade
  );
  const dispatch = useDispatch();
  const { defaultAccount } = config;

  // Fetch Trade Data
  useEffect(() => {
    if (defaultAccount) {
      dispatch(fetchTrade({ pg: 1, accId: defaultAccount }));
    }
  }, [defaultAccount, dispatch]);

  const [calendar, setCalendar] = useState({});
  const [monthlySummary, setMonthlySummary] = useState({});
  const [monthlyExtremes, setMonthlyExtremes] = useState({}); // { [month]: { maxProfitDay, maxLossDay } }
  const [monthlyAverages, setMonthlyAverages] = useState({}); // { [month]: { avgWin, avgLoss } }
  const [yearlyPnl, setYearlyPnl] = useState(0);

  useEffect(() => {
    if (isSuccess) {
      generateCalendar();
    }
  }, [tradeData, isSuccess]);

  const generateCalendar = () => {
    const startOfYear = dayjs().startOf("year");
    const today = dayjs();
    const days = [];

    for (
      let day = startOfYear;
      day.isBefore(today) || day.isSame(today);
      day = day.add(1, "day")
    ) {
      days.push(day);
    }

    const dailyPnl = days.map((day) => {
      const dayStr = day.format("YYYYMMDD");
      const tradesOfDay = tradeData.filter(
        (trade) =>
          dayjs(String(trade.closedDate), "YYYYMMDDHHmmss").format(
            "YYYYMMDD"
          ) === dayStr
      );
      const totalPnl = tradesOfDay.reduce(
        (sum, trade) => sum + Number(trade.pnl || 0),
        0
      );
      return {
        date: dayStr, // "YYYYMMDD"
        pnl: totalPnl, // signed number (can be negative)
        tradesCount: tradesOfDay.length,
      };
    });

    // Group per "MMMM YYYY"
    const groupedByMonth = dailyPnl.reduce((acc, day) => {
      const month = dayjs(day.date, "YYYYMMDD").format("MMMM YYYY");
      if (!acc[month]) acc[month] = [];
      acc[month].push(day);
      return acc;
    }, {});

    // Sort months (desc) and exclude future months
    const sortedCalendar = Object.keys(groupedByMonth)
      .filter(
        (month) =>
          dayjs(month, "MMMM YYYY").isBefore(today) ||
          dayjs(month, "MMMM YYYY").isSame(today, "month")
      )
      .sort((a, b) => dayjs(b, "MMMM YYYY").diff(dayjs(a, "MMMM YYYY")))
      .reduce((acc, key) => {
        acc[key] = groupedByMonth[key];
        return acc;
      }, {});
    setCalendar(sortedCalendar);

    // Monthly P&L totals
    const monthlyPnlSummary = dailyPnl.reduce((acc, day) => {
      const month = dayjs(day.date, "YYYYMMDD").format("MMMM YYYY");
      if (!acc[month]) acc[month] = 0;
      acc[month] += day.pnl;
      return acc;
    }, {});
    setMonthlySummary(monthlyPnlSummary);

    // Monthly extremes: largest profit day & largest loss day (absolute)
    const monthlyExt = Object.entries(groupedByMonth).reduce(
      (acc, [month, days]) => {
        let maxProfitDay = 0; // largest positive day sum
        let minDaySum = 0; // most negative day sum
        for (const d of days) {
          if (d.pnl > maxProfitDay) maxProfitDay = d.pnl;
          if (d.pnl < minDaySum) minDaySum = d.pnl;
        }
        acc[month] = {
          maxProfitDay, // number
          maxLossDay: Math.abs(minDaySum), // positive magnitude
        };
        return acc;
      },
      {}
    );
    setMonthlyExtremes(monthlyExt);

    // Monthly average win / average loss (per trade, not per day)
    // Group raw trades by month first
    const tradesByMonth = tradeData.reduce((acc, trade) => {
      const m = dayjs(String(trade.closedDate), "YYYYMMDDHHmmss").format(
        "MMMM YYYY"
      );
      if (!acc[m]) acc[m] = [];
      acc[m].push(trade);
      return acc;
    }, {});
    const monthlyAvgs = Object.keys(tradesByMonth).reduce((acc, m) => {
      const trades = tradesByMonth[m] || [];
      const wins = trades
        .filter((t) => Number(t.pnl) > 0)
        .map((t) => Number(t.pnl));
      const losses = trades
        .filter((t) => Number(t.pnl) < 0)
        .map((t) => Math.abs(Number(t.pnl)));

      const avg = (arr) =>
        arr.length ? arr.reduce((s, v) => s + v, 0) / arr.length : 0;

      acc[m] = {
        avgWin: avg(wins),
        avgLoss: avg(losses), // positive magnitude
      };
      return acc;
    }, {});
    setMonthlyAverages(monthlyAvgs);

    // Yearly total
    const totalYearlyPnl = dailyPnl.reduce((acc, day) => acc + day.pnl, 0);
    setYearlyPnl(totalYearlyPnl);
  };

  // --------- NEW: click-to-navigate (skip weekends) ----------
  function handleDayClick(yyyymmdd) {
    if (!yyyymmdd) return;
    const d = dayjs(yyyymmdd, "YYYYMMDD");
    const dayOfWeek = d.day(); // 0 Sun, 6 Sat
    if (dayOfWeek === 0 || dayOfWeek === 6) return; // skip weekends
    const routeDate = d.format("YYYY-MM-DD");
    navigate(`/trades/detail/date/${routeDate}`);
  }
  // -----------------------------------------------------------

  return (
    <Container>
      <Row className="mt-4">
        <Col md={12}>
          <Card className="tradeCard-boxShadow">
            <Card.Header as="h5" className="calendar-header">
              Monthly P&L Summary
            </Card.Header>
            <Card.Body className="calendar-body">
              <Table bordered>
                <thead>
                  <tr>
                    {Object.keys(monthlySummary).map((month) => (
                      <th key={month} style={{ textAlign: "center" }}>
                        {month}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    {Object.keys(monthlySummary).map((month) => (
                      <td key={month} className="calendar-cell">
                        <div
                          className={`pnl ${getPnlClass(
                            monthlySummary[month]
                          )}`}
                        >
                          ${monthlySummary[month].toFixed(2)}
                        </div>
                      </td>
                    ))}
                  </tr>
                </tbody>
              </Table>
              <div style={{ fontWeight: "bold", textAlign: "center" }}>
                Yearly P&L:{" "}
                <span className={`pnl ${getPnlClass(yearlyPnl)}`}>
                  ${yearlyPnl.toFixed(2)}
                </span>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Row className="mt-4">
        {Object.keys(calendar).map((month) => (
          <React.Fragment key={month}>
            <Col md={12} className="mb-4">
              <Card className="mb-4 tradeCard-boxShadow">
                <Card.Header as="h5" className="calendar-header">
                  {month}
                </Card.Header>
                <Card.Body className="calendar-body">
                  <Table bordered>
                    <thead>
                      <tr>
                        {[
                          "Sun",
                          "Mon",
                          "Tue",
                          "Wed",
                          "Thu",
                          "Fri",
                          "Sat",
                          "Total",
                        ].map((day) => (
                          <th key={day} style={{ textAlign: "center" }}>
                            {day}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {generateCalendarRows(calendar[month], month)}
                    </tbody>
                  </Table>

                  {/* Monthly P&L + Averages + Biggest Profit/Loss (Day) */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      gap: "2rem",
                      flexWrap: "wrap",
                      marginTop: "1rem",
                    }}
                  >
                    <ul
                      style={{
                        listStyle: "none",
                        padding: 0,
                        margin: "1rem auto",
                        textAlign: "left",
                        fontWeight: "500",
                      }}
                    >
                      <li>
                        Avg Win:{" "}
                        <span className="pnl positive">
                          $
                          {Number(
                            monthlyAverages[month]?.avgWin || 0
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </li>

                      <li>
                        Avg Loss:{" "}
                        <span className="pnl negative">
                          $
                          {Number(
                            monthlyAverages[month]?.avgLoss || 0
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </li>

                      <li>
                        Biggest Profit (Day):{" "}
                        <span className="pnl positive">
                          $
                          {Number(
                            monthlyExtremes[month]?.maxProfitDay || 0
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </li>

                      <li>
                        Biggest Loss (Day):{" "}
                        <span className="pnl negative">
                          $
                          {Number(
                            monthlyExtremes[month]?.maxLossDay || 0
                          ).toLocaleString(undefined, {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })}
                        </span>
                      </li>
                      <li>
                        Monthly P&L:{" "}
                        <span
                          className={`pnl ${getPnlClass(
                            monthlySummary[month]
                          )}`}
                        >
                          ${monthlySummary[month].toFixed(2)}
                        </span>
                      </li>
                    </ul>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          </React.Fragment>
        ))}
      </Row>
    </Container>
  );

  // Renders rows; each day cell is clickable if it's a weekday
  function generateCalendarRows(days, month) {
    const monthStart = dayjs(days[0].date, "YYYYMMDD").startOf("month");
    const monthEnd = dayjs(days[0].date, "YYYYMMDD").endOf("month");
    const firstDayOfWeek = monthStart.day();
    const totalDays = monthEnd.date();

    const calendarRows = [];
    let row = new Array(firstDayOfWeek).fill(null);
    let weeklyPnl = 0;

    for (let i = 1; i <= totalDays; i++) {
      const day = days.find((d) => dayjs(d.date, "YYYYMMDD").date() === i);
      row.push(
        day || {
          date: monthStart.date(i).format("YYYYMMDD"),
          pnl: 0,
          tradesCount: 0,
        }
      );
      weeklyPnl += day ? day.pnl : 0;

      if (row.length === 7) {
        row.push({ date: "", pnl: weeklyPnl, tradesCount: 0 }); // weekly total col
        calendarRows.push(row);
        row = [];
        weeklyPnl = 0;
      }
    }

    if (row.length > 0) {
      row = row.concat(new Array(7 - row.length).fill(null));
      row.push({ date: "", pnl: weeklyPnl, tradesCount: 0 });
      calendarRows.push(row);
    }

    return calendarRows.map((week, idx) => (
      <tr key={idx}>
        {week.map((day, dayIdx) => {
          // weekly total column or empty padding
          const isWeeklyTotal = day && !day.date;
          const isPadding = day === null;

          // real calendar day cell
          const isRealDay = day && !!day.date;
          const weekdayIdx = isRealDay
            ? dayjs(day.date, "YYYYMMDD").day()
            : null;
          const isWeekend = weekdayIdx === 0 || weekdayIdx === 6; // Sun or Sat

          const clickable = isRealDay && !isWeekend; // only weekdays navigate

          return (
            <td
              key={dayIdx}
              className={`calendar-cell ${clickable ? "clickable-day" : ""} ${
                isWeekend ? "weekend" : ""
              }`}
              onClick={() => (clickable ? handleDayClick(day.date) : undefined)}
            >
              {day && (
                <div className="calendar-day">
                  {isRealDay ? (
                    <>
                      <div className="calendar-date">
                        {dayjs(day.date, "YYYYMMDD").format("D")}
                      </div>
                      <div className={`pnl ${getPnlClass(day.pnl)}`}>
                        {day.pnl ? "$" + day.pnl.toFixed(2) : ""}
                      </div>
                      <div
                        className="trades-count"
                        style={
                          day.tradesCount === 0
                            ? { visibility: "hidden" }
                            : undefined
                        }
                      >
                        {day.tradesCount} trades
                      </div>
                    </>
                  ) : isWeeklyTotal ? (
                    <div className={`pnl ${getPnlClass(day.pnl)}`}>
                      ${day.pnl.toFixed(2)}
                    </div>
                  ) : null}
                </div>
              )}
            </td>
          );
        })}
      </tr>
    ));
  }

  function getPnlClass(pnl) {
    if (pnl > 0) return "positive";
    if (pnl < 0) return "negative";
    return "zero";
  }
};

export default TradeCalendar;
